import { createSlice, createSelector } from "@reduxjs/toolkit";

import {
  OnboardProperty,
  OnboardPropertyState,
  RootState,
} from "@/types/store";
import {
  generateBathroomFields,
  generateBedroomFields,
} from "@/utils/functions";
import { MAX_ADDITIONAL_BEDS } from "@/utils/constants";

import { blockNoticesReformat } from "@/utils/reformatApiData";
import { fromDateISOString } from "@/utils/dateFormatters";

import {
  createPropertyListing,
  createPropertyType,
  getAmenities,
  getUnfinishedProperties,
} from "@/api/properties/thunks";
import { Amenity } from "@/api/properties/types";
import { USStates } from "@/utils/countryStates";
import { PropertyStatus, PropertyType, SmartLockType } from "@/types/enums";
import { PropertyFromBE } from "@/types/properties";
import {
  createCalendarBlock,
  getPropertyCalendarBlocks,
  updateCalendarBlock,
  getPropertyCalendarBlocksYearOverview,
} from "@/api/calendarBlocks/thunks";
import {
  isHalfBathroom,
  parseBathroomsFromBE,
  parseBedroomsFromBE,
} from "@/utils/propertyConverters";

const defaultBedroomCount = 3;
const defaultFullBathroomCount = 1;
const defaultHalfBathroomCount = 1;

const createInitialPropertyState = (): OnboardProperty => ({
  id: "",
  isListed: true,
  propertyLink1: "",
  propertyLink2: "",
  propertyName: "",
  propertyType: PropertyType.SINGLE_FAMILY_HOME,
  bedrooms: defaultBedroomCount,
  bathrooms: 0,
  maxGuests: 1,
  parkingSpaces: 2,
  fullBathrooms: defaultFullBathroomCount,
  halfBathrooms: defaultHalfBathroomCount,

  // Dynamically generated bedroom fields
  bedroomFields: generateBedroomFields(defaultBedroomCount),

  // Dynamically generated bathroom fields
  bathroomFields: generateBathroomFields(
    defaultFullBathroomCount,
    defaultHalfBathroomCount
  ),
  amenities: undefined,
  wifiName: "",
  wifiPassword: "",
  isPetFriendly: false,
  maxPets: null,

  // Property description
  description: "",

  // Vendor information
  // Pest Control
  pestControlCompany: "",
  pestControlPhone: "",

  // Pool
  poolCompany: "",
  poolPhone: "",
  poolService: [],

  // HVAC
  hvacCompany: "",
  hvacPhone: "",
  hvacService: [],

  // House Management
  houseManagerName: "",
  houseManagerPhone: "",

  // Landscaping
  landscapingCompany: "",
  landscapingPhone: "",
  landscapingService: [],

  // Array for extra additional vendors
  additionalVendors: [],

  // Address information
  streetAddress: "",
  apartmentUnit: "",
  state: "",
  city: "",
  zipCode: "",
  country: "",

  // Additional property details
  size: 0,
  totalBeds: 6,

  // Additional information
  guestCleaning: false,
  smartLock: SmartLockType.INTEGRATION,
  lockPin: "",
  damageWaiver: false,
  status: PropertyStatus.CREATE_LISTING,
  monthBlockNotices: [],
  yearBlockNotices: {},
});

const initialState: OnboardPropertyState = {
  properties: {},
  currentPropertyId: null,
  currentUnitId: null,
};

export const OnboardPropertySlice = createSlice({
  name: "OnboardPropertySlice",
  initialState,
  reducers: {
    setPropertyListingData: (
      state,
      { payload: { isListed, propertyLink1, propertyLink2 } }
    ) => {
      if (!state.currentPropertyId) return;
      const currentProperty = state.properties[state.currentPropertyId];
      currentProperty.isListed = isListed;
      currentProperty.propertyLink1 = propertyLink1;
      currentProperty.propertyLink2 = propertyLink2;
      currentProperty.updatedAt = new Date().toISOString();
    },
    setPropertyInformationData: (
      state,
      {
        payload: {
          propertyName,
          streetAddress,
          apartmentUnit,
          state: payloadState,
          city,
          zipCode,
          country,
        },
      }
    ) => {
      if (!state.currentPropertyId) return;
      const currentProperty = state.properties[state.currentPropertyId];
      currentProperty.propertyName = propertyName;
      currentProperty.streetAddress = streetAddress;
      currentProperty.apartmentUnit = apartmentUnit;
      currentProperty.state = payloadState;
      currentProperty.city = city;
      currentProperty.zipCode = zipCode;
      currentProperty.country = country;
      currentProperty.updatedAt = new Date().toISOString();
    },
    setPropertyFeaturesData: (
      state,
      { payload: { wifiName, wifiPassword, isPetFriendly, maxPets } }
    ) => {
      if (!state.currentPropertyId) return;
      const currentProperty = state.properties[state.currentPropertyId];
      currentProperty.wifiName = wifiName;
      currentProperty.wifiPassword = wifiPassword;
      currentProperty.isPetFriendly = isPetFriendly;
      currentProperty.maxPets = maxPets ?? null;
      currentProperty.updatedAt = new Date().toISOString();
    },
    setAmenitiesList: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      state.properties[state.currentPropertyId].amenities = payload;
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setPropertyDetailsData: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      const currentProperty = state.properties[state.currentPropertyId];
      currentProperty.size = payload.size;
      currentProperty.totalBeds = payload.totalBeds;
      currentProperty.parkingSpaces = payload.parkingSpaces;
      currentProperty.bedrooms = payload.bedrooms;
      currentProperty.fullBathrooms = payload.fullBathrooms;
      currentProperty.halfBathrooms = payload.halfBathrooms;
      currentProperty.bedroomFields = {};

      Array.from({ length: payload.fullBathrooms }).forEach((_, index) => {
        currentProperty.bathroomFields[`fullBathroom${index + 1}Type`] =
          payload[`fullBathroom${index + 1}Type`];
        currentProperty.bathroomFields[`fullBathroom${index + 1}Info`] =
          payload[`fullBathroom${index + 1}Info`];
        currentProperty.bathroomFields[`fullBathroom${index + 1}Bedrooms`] =
          payload[`fullBathroom${index + 1}Bedrooms`];
      });

      Array.from({ length: payload.halfBathrooms }).forEach((_, index) => {
        currentProperty.bathroomFields[`halfBathroom${index + 1}Type`] =
          payload[`halfBathroom${index + 1}Type`];
        currentProperty.bathroomFields[`halfBathroom${index + 1}Info`] =
          payload[`halfBathroom${index + 1}Info`];
        currentProperty.bathroomFields[`halfBathroom${index + 1}Bedrooms`] =
          payload[`halfBathroom${index + 1}Bedrooms`];
      });

      Array.from({ length: payload.bedrooms }).forEach((_, index) => {
        currentProperty.bedroomFields[`bedroom${index + 1}Info`] =
          payload[`bedroom${index + 1}Info`];
        currentProperty.bedroomFields[`bedroom${index + 1}Quantity`] =
          payload[`bedroom${index + 1}Quantity`];
        currentProperty.bedroomFields[`bedroom${index + 1}Type`] =
          payload[`bedroom${index + 1}Type`];

        for (let i = 1; i <= MAX_ADDITIONAL_BEDS + 1; i++) {
          currentProperty.bedroomFields[`bedroom${index + 1}Quantity${i}`] =
            payload[`bedroom${index + 1}Quantity${i}`];
          currentProperty.bedroomFields[`bedroom${index + 1}Type${i}`] =
            payload[`bedroom${index + 1}Type${i}`];
        }
      });
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setPropertyDescriptionData: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      state.properties[state.currentPropertyId].description =
        payload.description;
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setVendorInfoData: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      const currentProperty = state.properties[state.currentPropertyId];
      currentProperty.pestControlCompany = payload.pestControlCompany;
      currentProperty.pestControlPhone = payload.pestControlPhone;
      currentProperty.poolCompany = payload.poolCompany;
      currentProperty.poolPhone = payload.poolPhone;
      currentProperty.poolService = payload.poolService;
      currentProperty.hvacCompany = payload.hvacCompany;
      currentProperty.hvacPhone = payload.hvacPhone;
      currentProperty.hvacService = payload.hvacService;
      currentProperty.houseManagerName = payload.houseManagerName;
      currentProperty.houseManagerPhone = payload.houseManagerPhone;
      currentProperty.landscapingCompany = payload.landscapingCompany;
      currentProperty.landscapingPhone = payload.landscapingPhone;
      currentProperty.landscapingService = payload.landscapingService;
      currentProperty.additionalVendors = payload.additionalVendors;
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setAdditionalInfoData: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      const currentProperty = state.properties[state.currentPropertyId];
      currentProperty.guestCleaning = payload.guestCleaning;
      currentProperty.smartLock = payload.smartLock;
      currentProperty.lockPin = payload.lockPin;
      currentProperty.damageWaiver = payload.damageWaiver;
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setPropertyUnitsData: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      state.properties[state.currentPropertyId].propertyUnits =
        payload.propertyUnits;
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setPropertyTotalValues: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      const currentProperty = state.properties[state.currentPropertyId];
      currentProperty.size = payload.size;
      currentProperty.totalBeds = payload.totalBeds;
      currentProperty.parkingSpaces = payload.parkingSpaces;
      currentProperty.bedrooms = payload.bedrooms;
      currentProperty.fullBathrooms = payload.fullBathrooms;
      currentProperty.halfBathrooms = payload.halfBathrooms;
      currentProperty.updatedAt = new Date().toISOString();
    },
    setMonthBlockNotices: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      state.properties[state.currentPropertyId].monthBlockNotices = payload;
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setYearBlockNotices: (state, { payload }) => {
      if (!state.currentPropertyId) return;
      state.properties[state.currentPropertyId].yearBlockNotices = payload;
      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    setCurrentPropertyId: (state, { payload }) => {
      if (payload in state.properties) {
        state.currentPropertyId = payload;
      }
    },
    setCurrentUnitId: (state, { payload }) => {
      if (payload === "none") {
        state.currentUnitId = null;
      } else {
        state.currentUnitId = payload;
      }
    },
    setSelectedAmenities: (state, { payload }) => {
      if (!state.currentPropertyId) return;

      // Amenities aren't loaded until the amenities step is reached.
      const amenities = state.properties[state.currentPropertyId].amenities;
      if (!amenities) return;

      const currentAmenities = amenities.amenities;
      const hasChanges = currentAmenities.some(
        (amenity: Amenity) => amenity.selected !== payload.includes(amenity.id)
      );

      if (!hasChanges) return; // Skip update if no changes

      amenities.amenities = currentAmenities.map((amenity: Amenity) => ({
        ...amenity,
        selected: payload.includes(amenity.id),
      }));

      state.properties[state.currentPropertyId].updatedAt =
        new Date().toISOString();
    },
    clearOnboardPropertyData: () => {
      return initialState;
    },
    clearCurrentProperty: (state) => {
      if (!state.currentPropertyId) return;
      delete state.properties[state.currentPropertyId];
      state.currentPropertyId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPropertyType.fulfilled, (state, { payload }) => {
        state.properties[payload.id] = createInitialPropertyState();
        state.currentPropertyId = payload.id;
        if (!state.currentPropertyId) return;
        const currentProperty = state.properties[state.currentPropertyId];
        currentProperty.id = payload.id;
        currentProperty.propertyType = payload.type;
      })
      .addCase(getUnfinishedProperties.fulfilled, (state, { payload }) => {
        // Handle both new format (object with properties) and old format (array)
        const propertiesArray = payload.properties ?? payload;
        const newProperties = propertiesArray.reduce(
          (acc: Record<string, OnboardProperty>, property: PropertyFromBE) => {
            const existingUpdatedAt = state.properties[property.id]?.updatedAt;
            const newUpdatedAt = property.updatedAt;
            if (
              existingUpdatedAt &&
              newUpdatedAt &&
              new Date(existingUpdatedAt) > new Date(newUpdatedAt)
            ) {
              acc[property.id] = state.properties[property.id];
              return acc;
            }

            acc[property.id] = {
              id: property.id,
              isListed: !!property.propertyLink1 && !!property.propertyLink2,
              propertyLink1: property.propertyLink1 ?? "",
              propertyLink2: property.propertyLink2 ?? "",
              propertyName: property.name ?? "",
              propertyType: property.type ?? "",
              bedrooms: property.numberOfBedrooms ?? 0,
              bathrooms:
                property.numberOfFullBathrooms ??
                property.numberOfHalfBathrooms ??
                0,
              bedroomFields: parseBedroomsFromBE(property.bedrooms, property.numberOfBedrooms ?? 0),
              bathroomFields: parseBathroomsFromBE(
                property.bathrooms,
                property.bedrooms,
                property.numberOfFullBathrooms ?? 0,
                property.numberOfHalfBathrooms ?? 0
              ),
              maxGuests: 1,
              parkingSpaces: property.numberOfParkingSpots ?? 2,
              fullBathrooms: property.numberOfFullBathrooms ?? 1,
              halfBathrooms: property.bathrooms.filter((bathroom) =>
                isHalfBathroom(bathroom)
              ).length,
              amenities: {
                groups: [],
                // Carries the whole amenity so the list keeps its labels.
                amenities: property.streamlineAmenities.map(
                  (amenity: Amenity) => ({
                    ...amenity,
                    selected: true,
                  })
                ),
              },
              wifiName: property.wifiName ?? "",
              wifiPassword: property.wifiPassword ?? "",
              isPetFriendly: property.petFriendly ?? false,
              maxPets: property.maxPets ?? null,
              description: property.description ?? "",
              pestControlCompany: property.pestControlCompanyName ?? "",
              pestControlPhone: property.pestControlCompanyPhoneNumber ?? "",
              poolCompany: property.poolCompanyName ?? "",
              poolPhone: property.poolCompanyPhoneNumber ?? "",
              poolService: property.poolServiceDay ?? [],
              hvacCompany: property.hvac ?? "",
              hvacPhone: property.hvacPhoneNumber ?? "",
              hvacService: property.hvacTrashPickupDay ?? [],
              houseManagerName: property.houseManagerName ?? "",
              houseManagerPhone: property.houseManagerPhoneNumber ?? "",
              landscapingCompany: property.landscapingCompanyName ?? "",
              landscapingPhone: property.landscapingCompanyPhoneNumber ?? "",
              landscapingService: property.landscapingServiceDay ?? [],
              additionalVendors: property.additionalVendors.map((vendor) => ({
                type: vendor.type,
                name: vendor.companyName,
                phone: vendor.companyPhoneNumber,
                info: vendor.otherInformation,
              })),
              streetAddress: property.streetAddress ?? "",
              apartmentUnit: property.apartment ?? "",
              state:
                Object.keys(USStates).find(
                  (key) =>
                    USStates[key as keyof typeof USStates] === property.state
                ) ?? "",
              city: property.city ?? "",
              zipCode: property.zipCode ?? "",
              country: property.country ?? "",
              size: property.size ?? 0,
              totalBeds: property.sleepTotal ?? 0,
              guestCleaning: property.guestCleaningService ?? false,
              smartLock: (() => {
                if (property.smartLock && !property.lockPin) {
                  return SmartLockType.INTEGRATION;
                }
                if (!property.smartLock && property.lockPin) {
                  return SmartLockType.STATIC_PIN;
                }
                if (!property.smartLock && !property.lockPin) {
                  return SmartLockType.LAST_FOUR_DIGITS;
                }
                return SmartLockType.INTEGRATION;
              })(),
              lockPin: property.lockPin ?? "",
              damageWaiver: property.damageWaiverService ?? false,
              propertyUnits: property.units.map((unit, index) => ({
                id: unit.id,
                index: index,
                title: unit.name,
                propertyType: "Apartment",
                unitDescription: unit.description,
                size: unit.size,
                totalBeds: unit.sleepTotal,
                bedrooms: unit.numberOfBedrooms,
                fullBathrooms: unit.numberOfFullBathrooms,
                halfBathrooms: unit.bathrooms.filter((bathroom) =>
                  isHalfBathroom(bathroom)
                ).length,
                parkingSpaces: unit.numberOfParkingSpots,
                // TODO: Mapping amenities "on field" since they need to be fresh fetched from Streamline
                amenities: { groups: [], amenities: [] },
                isPetFriendly: unit.petFriendly,
                maxPets: unit.maxPets ?? null,
                wifiName: unit.wifiName ?? "",
                wifiPassword: unit.wifiPassword ?? "",
                ...parseBedroomsFromBE(unit.bedrooms, unit.numberOfBedrooms ?? 0),
                ...parseBathroomsFromBE(unit.bathrooms, unit.bedrooms, unit.numberOfFullBathrooms ?? 0, unit.numberOfHalfBathrooms ?? 0),
              })),
              status: property.status,
              monthBlockNotices: [],
              yearBlockNotices: {},
              updatedAt: property.updatedAt,
            };
            return acc;
          },
          {}
        );

        // Merge new properties with existing state
        state.properties = {
          ...state.properties,
          ...newProperties,
        };
      })
      .addCase(createPropertyListing.fulfilled, (state, { payload }) => {
        state.properties[payload.id].status = PropertyStatus.PHOTOS_AND_VIDEO;
        if (payload.units.length > 0) {
          state.properties[payload.id].propertyUnits = state.properties[
            payload.id
          ].propertyUnits?.map((unit) => {
            // Keep the existing id when the response has no unit by that name,
            // so an unmatched unit isn't left without one.
            const createdUnit = payload.units.find(
              (u) => u.name === unit.title
            );
            return createdUnit ? { ...unit, id: createdUnit.id } : unit;
          });
          state.properties[payload.id].updatedAt = new Date().toISOString();
        }
      })
      .addCase(
        getPropertyCalendarBlocksYearOverview.fulfilled,
        (state, { payload }) => {
          if (!state.currentPropertyId) return;
          state.properties[state.currentPropertyId].yearBlockNotices = payload;
          state.properties[state.currentPropertyId].updatedAt =
            new Date().toISOString();
        }
      )
      .addCase(getPropertyCalendarBlocks.fulfilled, (state, { payload }) => {
        if (!state.currentPropertyId) return;
        state.properties[state.currentPropertyId].monthBlockNotices =
          blockNoticesReformat(payload);
        state.properties[state.currentPropertyId].updatedAt =
          new Date().toISOString();
      })
      .addCase(createCalendarBlock.fulfilled, (state, { payload }) => {
        if (!state.currentPropertyId) return;
        state.properties[state.currentPropertyId].monthBlockNotices.push({
          block: payload,
          label:
            payload.type === "OWNER_BLOCK" ? "Owner Block" : "Previous Booking",
          color:
            payload.type === "OWNER_BLOCK"
              ? "bg-owner-block text-white"
              : "bg-previous-booking text-white",
        });

        const startDate = fromDateISOString(payload.start);
        const monthName = startDate
          .toLocaleString("default", { month: "long" })
          .toUpperCase();

        const currentYearNotices =
          state.properties[state.currentPropertyId].yearBlockNotices;
        state.properties[state.currentPropertyId].yearBlockNotices = {
          ...currentYearNotices,
          [monthName]: (currentYearNotices[monthName] || 0) + 1,
        };
        state.properties[state.currentPropertyId].updatedAt =
          new Date().toISOString();
      })
      .addCase(updateCalendarBlock.fulfilled, (state, { payload }) => {
        if (!state.currentPropertyId) return;
        const blockNotices =
          state.properties[state.currentPropertyId].monthBlockNotices;
        const blockIndex = blockNotices.findIndex(
          (notice) => notice.block.id === payload.id
        );
        if (blockIndex !== -1) {
          blockNotices[blockIndex] = {
            block: payload,
            label:
              payload.type === "OWNER_BLOCK"
                ? "Owner Block"
                : "Previous Booking",
            color:
              payload.type === "OWNER_BLOCK"
                ? "bg-owner-block text-white"
                : "bg-previous-booking text-white",
          };
        }
        state.properties[state.currentPropertyId].updatedAt =
          new Date().toISOString();
      })
      .addCase(getAmenities.fulfilled, (state, { payload }) => {
        state.serverAmenities = payload;
        if (!state.currentPropertyId) return;

        const currentAmenities =
          state.properties[state.currentPropertyId].amenities;
        // Copy the `selected` value from an existing amenity if it has been
        // selected
        state.properties[state.currentPropertyId].amenities = {
          ...state.properties[state.currentPropertyId].amenities,
          amenities: payload.amenities.map((amenity: Amenity) => ({
            ...amenity,
            selected:
              currentAmenities?.amenities?.some(
                (a: Amenity) => a.id === amenity.id && a.selected
              ) ?? false,
          })),
          groups: payload.groups,
        };

        state.properties[state.currentPropertyId].updatedAt =
          new Date().toISOString();
      });
  },
});

export const {
  setPropertyListingData,
  setPropertyInformationData,
  setPropertyFeaturesData,
  setAmenitiesList,
  setPropertyDetailsData,
  setPropertyDescriptionData,
  setVendorInfoData,
  setAdditionalInfoData,
  setPropertyUnitsData,
  setPropertyTotalValues,
  setMonthBlockNotices,
  setYearBlockNotices,
  setCurrentPropertyId,
  setCurrentUnitId,
  setSelectedAmenities,
  clearOnboardPropertyData,
  clearCurrentProperty,
} = OnboardPropertySlice.actions;

export default OnboardPropertySlice.reducer;

export const selectAdditionalBeds = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
  ],
  (properties, currentId) => {
    if (!currentId || !properties[currentId]) return {};
    const currentProperty = properties[currentId];
    const retVal: Record<string, number> = {};
    Array.from({ length: currentProperty.bedrooms }).forEach((_, index) => {
      retVal[`bedroom${index + 1}`] = 0;
      for (let i = 2; i <= MAX_ADDITIONAL_BEDS + 1; i++) {
        if (currentProperty.bedroomFields[`bedroom${index + 1}Type${i}`]) {
          retVal[`bedroom${index + 1}`] = Math.max(
            retVal[`bedroom${index + 1}`],
            i - 1
          );
        }
      }
    });
    return retVal;
  }
);

export const selectAdditionalBedsByUnit = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
    (_state: RootState, unitIndex: number) => unitIndex,
  ],
  (properties, currentId, unitIndex) => {
    if (!currentId || !properties[currentId]) return {};
    const currentProperty = properties[currentId];
    if (
      !currentProperty.propertyUnits ||
      !currentProperty.propertyUnits[unitIndex]
    ) {
      return {};
    }
    const unit = currentProperty.propertyUnits[unitIndex];
    const retVal: Record<string, number> = {};

    Array.from({ length: unit.bedrooms || 0 }).forEach((_, index) => {
      retVal[`bedroom${index + 1}`] = 0;
      for (let i = 2; i <= MAX_ADDITIONAL_BEDS + 1; i++) {
        if (unit[`bedroom${index + 1}Type${i}`]) {
          retVal[`bedroom${index + 1}`] = Math.max(
            retVal[`bedroom${index + 1}`],
            i - 1
          );
        }
      }
    });
    return retVal;
  }
);

export const selectCurrentProperty = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
  ],
  (properties, currentId) => (currentId ? properties[currentId] : null)
);

export const selectAmenities = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
  ],
  (properties, currentId) =>
    currentId ? properties[currentId].amenities : undefined
);

export const selectPropertyUnits = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
  ],
  (properties, currentId) =>
    currentId ? properties[currentId].propertyUnits : []
);

export const selectPropertyType = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
  ],
  // Annotated so the empty fallback doesn't widen the union to string.
  (properties, currentId): PropertyType | "" =>
    currentId ? properties[currentId].propertyType : ""
);

export const selectPropertyName = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
  ],
  (properties, currentId) =>
    currentId ? properties[currentId].propertyName : ""
);

export const selectStatus = createSelector(
  [
    (state: RootState) => state.OnboardProperty.properties,
    (state: RootState) => state.OnboardProperty.currentPropertyId,
  ],
  (properties, currentId) =>
    currentId ? properties[currentId].status : undefined
);

